import { Button, Icon, Spinner, Tooltip, TooltipContent, TooltipTrigger } from "@qw/ui";
import { useInboxRail } from "@/inbox/use-inbox-rail";
import type { InboxInsured } from "@/providers/use-inbox-insured";

interface MiniAppHeaderProps {
  insured: InboxInsured | null | undefined;
  loading: boolean;
}

/**
 * The rail header row: a way back to the Book, and the insured's name.
 *
 * Everything the broker can do to the insured is one row down, beside the tabs,
 * in `MiniAppActions`. The Book panel is 280px by default and can be dragged to
 * 220px, which is not enough for a name and four controls on one line — the name
 * was down to a few characters at the floor. Giving the name the whole row and
 * the controls the tab row's spare width costs no vertical space at all.
 *
 * Back to book is the exception that stays up here: it is navigation out of the
 * insured rather than something to do to it, so it sits where the same control
 * sits on the email beside it (`conversation-header.tsx`). Merged mode only —
 * in split mode the Book never left the screen.
 */
export const MiniAppHeader = ({ insured, loading }: MiniAppHeaderProps) => {
  const { isMergedRail, pinBookInRail } = useInboxRail();

  return (
    <div className="flex min-w-0 flex-auto items-center gap-2">
      {isMergedRail && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label="Back to book"
              className="flex-none"
              display="icon"
              onClick={pinBookInRail}
              size="sm"
              variant="ghost"
            >
              <Icon icon="arrow_back" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Back to book</TooltipContent>
        </Tooltip>
      )}
      {loading && <Spinner className="flex-none text-xs" />}
      <span className="min-w-0 flex-auto truncate font-medium text-base">{insured?.name}</span>
    </div>
  );
};
